import type { Store } from '@/types/store';
import { configSettings } from '@/lib/config';

interface AddFunnelToStoreUrlOptions {
    storeId: number;
    funnelId: number;
    funnelUrl: string;
    funnelName?: string;
    funnelImage?: string;
}

/**
 * Three-tier funnel display name fallback:
 * 1. store.data.name → use it
 * 2. store.first_page_title → "Funnel - {title}"
 * 3. fallback → "Funnel - Draft"
 */
export function getFunnelDisplayName(store: Store): string {
    if (store.data?.name) return store.data.name;
    if (store.first_page_title) return `Funnel - ${store.first_page_title}`;
    return 'Funnel - Draft';
}

/**
 * Generate funnel URL with or without https
 */
export function genFunnelURL(slug: string, username: string, noHttps = false): string {
    if (noHttps) {
        const domain = (configSettings.noHttpsFrontDomain || 'stan.store').replace(/\/+$/, '');
        return `${domain}/${username}/${slug}`;
    }
    const domain = (configSettings.frontDomain || 'https://stan.store').replace(/\/+$/, '');
    return `${domain}/${username}/${slug}`;
}

/**
 * Build the legacy (Vue) URL/Media product create URL with funnel context
 * pre-filled. The Vue PageLinkURL component reads these query params and
 * seeds the new product accordingly.
 *
 * My Store hasn't been migrated to React yet, so we always prefix with the
 * legacy admin domain — otherwise a same-origin redirect on v2 would 404.
 */
export function buildAddFunnelToStoreUrl({
    storeId,
    funnelId,
    funnelUrl,
    funnelName,
    funnelImage,
}: AddFunnelToStoreUrlOptions): string {
    const params = new URLSearchParams();
    params.set('location', 'store');
    params.set('funnelId', String(funnelId));
    params.set('prefillUrl', funnelUrl);
    if (funnelName) params.set('prefillTitle', funnelName);
    if (funnelImage) params.set('prefillImage', funnelImage);

    const path = `/stores/${storeId}/page/link/create?${params.toString()}`;
    const legacyDomain = configSettings.legacyAdminDomain?.replace(/\/+$/, '') ?? '';
    return `${legacyDomain}${path}`;
}
